// Arena effects (lane C, backlog C-7, C-12, C-19, C-20; territory-visuals.md §7–§9):
// - break-up: one square particle per BURST_SPACING cells along the lost land's outline and the
//   trail, moving perpendicular to its segment (random side) at 4.2–21 cells/s (75 % at a tenth
//   of that), 0.33 cell × 1–1.5, life 500–1000 ms shrinking linearly to 30 %, spin 2π–4π rad/s,
//   colour from the victim's 9 particle shades; caps 1,500 per death and 3,000 live (the spacing
//   grows above the cap). No shake, no flash.
// - chewing chips: one per rendered frame while a head stands on enemy land (cap 90): 0.33 cell
//   squares that live 300 ms, thrown forward 15–30 cells/s ±6° and decelerating at 90 cells/s².
// - floating labels over the own head ("+X.XX%", the kill word in the victim's colour, "+$Z"):
//   1000 ms (collect 800 ms), drift up 0.42 cell and back, alpha 1 + (r − 1)^5. Only labels born
//   in the same tick stack (1.7 cells apart); a later one starts at the bottom slot again and
//   overlaps the fading one, as in the reference (SF-10).
// - the capture flash (C-20): 90 ms white α 0.12 over the gained land, own captures only.
// - the exit fade (UI §4.3): a cash-out or forced exit's land fades from its main colour to
//   nothing over LAND_FADE_MS (the land itself is already neutral), so a bank never reads as a
//   death (no break-up).
// The flash and the fade follow the same smoothed outline as the land (contour.ts), not the cell
// squares, so neither shows a staircase over rounded land.
// Deterministic from a seeded generator (tests); times are performance.now() ms.

import { ARENA_CLIENT, createRng, type Rgb } from '@ballio/shared';
import type { Ctx2D } from '../render/canvas.js';
import { css, cssAlpha } from '../render/theme.js';
import type { ArenaView } from './camera.js';
import { cellSetContours, pathPolygons, type Polygons } from './contour.js';
import { shadesOf } from './shades.js';

export const BURST_SPACING = 0.83;
export const BURST_MAX_PER_DEATH = 1500;
export const BURST_MAX_LIVE = 3000;
export const CHIP_MAX = 90;
export const CHIP_LIFE_MS = 300;
export const LAND_FADE_MS = 250;
/** Land fades running at once, at most (each holds the exiting land's cells). */
const LAND_FADE_MAX = 8;
const TAU = Math.PI * 2;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  angle: number;
  spin: number;
  bornMs: number;
  lifeMs: number;
  color: string;
  /** Chips decelerate; burst particles keep their speed. */
  decel: number;
  /** Final size share (burst 0.3, chips 0). */
  endScale: number;
}

export interface FloatingLabel {
  readonly text: string;
  readonly color: Rgb;
  readonly bornMs: number;
  readonly lifeMs: number;
  /** Stack slot (0 = lowest). */
  readonly slot: number;
  /** Font size multiplier of the base 30 px × s. */
  readonly size: number;
}

/** Label alpha at `ageMs` of `lifeMs`: 1 + (r − 1)^5 with r = remaining share. */
export function labelAlpha(ageMs: number, lifeMs: number = ARENA_CLIENT.labelMs): number {
  if (ageMs < 0) return 1;
  if (ageMs >= lifeMs) return 0;
  const r = 1 - ageMs / lifeMs;
  return Math.max(0, Math.min(1, 1 + (r - 1) ** 5));
}

/** Label vertical drift (cells, negative = up) at `ageMs`: up 0.42 cell at the midpoint, back at the end. */
export function labelDrift(ageMs: number, lifeMs: number = ARENA_CLIENT.labelMs): number {
  const t = Math.min(1, Math.max(0, ageMs / lifeMs));
  return (-10 * t + 10 * t * t) / 6;
}

/** Particle count and spacing for `lengthCells` of outline under the per-death cap. */
export function burstSpacing(lengthCells: number): number {
  if (!(lengthCells > 0)) return BURST_SPACING;
  return Math.max(BURST_SPACING, lengthCells / BURST_MAX_PER_DEATH);
}

export class ArenaEffects {
  private readonly particles: Particle[] = [];
  private readonly chips: Particle[] = [];
  readonly labels: FloatingLabel[] = [];
  private flash: { polys: Polygons; atMs: number } | null = null;
  private readonly fades: { polys: Polygons; atMs: number; color: Rgb }[] = [];
  private readonly rand: () => number;
  /** Spawning stops while false (hidden tab, reduced motion keeps labels only). */
  motion = true;

  constructor(seed = 0x5eed) {
    const rng = createRng(seed);
    this.rand = () => rng.next();
  }

  get particleCount(): number {
    return this.particles.length;
  }

  get chipCount(): number {
    return this.chips.length;
  }

  clear(): void {
    this.particles.length = 0;
    this.chips.length = 0;
    this.labels.length = 0;
    this.flash = null;
    this.fades.length = 0;
  }

  /** Land fades running (tests). */
  get fadeCount(): number {
    return this.fades.length;
  }

  /** The exit fade of a cash-out / forced exit's land `cells` (grid indices) in `color`. */
  landFade(cells: readonly number[], cols: number, color: Rgb, nowMs: number): void {
    if (cells.length === 0) return;
    if (this.fades.length >= LAND_FADE_MAX) this.fades.shift();
    this.fades.push({ polys: cellSetContours(cells, cols), atMs: nowMs, color });
  }

  /** The exit fades (call where the land top is drawn, before the trails). */
  drawFades(ctx: Ctx2D, view: ArenaView, nowMs: number, liftPx: number): void {
    if (this.fades.length === 0) return;
    const s = view.scale;
    const ox = view.width / 2 - view.cx * s;
    const oy = view.height / 2 - view.cy * s - liftPx;
    for (const f of this.fades) {
      const a = 1 - (nowMs - f.atMs) / LAND_FADE_MS;
      if (a <= 0 || a > 1) continue;
      ctx.fillStyle = cssAlpha(f.color, a);
      ctx.beginPath();
      pathPolygons(ctx, f.polys, ox, oy, s);
      ctx.fill();
    }
  }

  /**
   * A death break-up from the victim's land edges (flat [x, y, nx, ny, …]) and trail cell centres
   * (flat [x, y, …]), culled to `view` (+2 cells).
   */
  burst(
    edges: readonly number[],
    trail: readonly number[],
    color: Rgb,
    nowMs: number,
    inView: (x: number, y: number) => boolean,
  ): number {
    if (!this.motion) return 0;
    const shades = shadesOf(color).particles.map((c) => css(c));
    const points: number[] = [];
    // Edges are 1 cell long: keep each with probability 1 / spacing (≈ one per 0.83 cell).
    const edgeCount = edges.length / 4;
    const trailCount = trail.length / 2;
    const spacing = burstSpacing(edgeCount + trailCount);
    const keep = 1 / spacing;
    for (let i = 0; i < edges.length; i += 4) {
      let n = Math.floor(keep) + (this.rand() < keep - Math.floor(keep) ? 1 : 0);
      for (; n > 0; n--) {
        const x = edges[i] as number;
        const y = edges[i + 1] as number;
        if (!inView(x, y)) continue;
        const nx = edges[i + 2] as number;
        const ny = edges[i + 3] as number;
        // Spread along the edge.
        const along = this.rand() - 0.5;
        points.push(x + ny * along, y - nx * along, nx, ny);
      }
    }
    for (let i = 0; i + 3 < trail.length; i += 2) {
      const x = trail[i] as number;
      const y = trail[i + 1] as number;
      if (!inView(x, y)) continue;
      const dx = (trail[i + 2] as number) - x;
      const dy = (trail[i + 3] as number) - y;
      const d = Math.hypot(dx, dy) || 1;
      let n = Math.floor(keep) + (this.rand() < keep - Math.floor(keep) ? 1 : 0);
      for (; n > 0; n--) points.push(x, y, -dy / d, dx / d);
    }
    let made = 0;
    for (let i = 0; i < points.length; i += 4) {
      if (this.particles.length >= BURST_MAX_LIVE || made >= BURST_MAX_PER_DEATH) break;
      const side = this.rand() < 0.5 ? -1 : 1;
      let speed = 4.2 + this.rand() * (21 - 4.2);
      if (this.rand() < 0.75) speed *= 0.1;
      const nx = (points[i + 2] as number) * side;
      const ny = (points[i + 3] as number) * side;
      this.particles.push({
        x: points[i] as number,
        y: points[i + 1] as number,
        vx: nx * speed,
        vy: ny * speed,
        size: 0.33 * (1 + this.rand() * 0.5),
        angle: this.rand() * TAU,
        spin: (this.rand() < 0.5 ? -1 : 1) * (TAU + this.rand() * TAU),
        bornMs: nowMs,
        lifeMs: 500 + this.rand() * 500,
        color: shades[Math.floor(this.rand() * shades.length)] as string,
        decel: 0,
        endScale: 0.3,
      });
      made++;
    }
    return made;
  }

  /** One chewing chip for a head at (x, y) heading `heading` over land of colour `landColor`. */
  chip(x: number, y: number, heading: number, landColor: Rgb, nowMs: number): void {
    if (!this.motion || this.chips.length >= CHIP_MAX) return;
    const shades = shadesOf(landColor).particles;
    const fx = Math.cos(heading);
    const fy = Math.sin(heading);
    const side = (this.rand() * 2 - 1) * 0.67;
    const a = heading + ((this.rand() * 2 - 1) * 6 * Math.PI) / 180;
    const speed = 15 + this.rand() * 15;
    this.chips.push({
      x: x + fx * 0.67 - fy * side,
      y: y + fy * 0.67 + fx * side - ARENA_CLIENT.liftCells,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed,
      size: 0.33 * (0.75 + this.rand() * 0.5),
      angle: this.rand() * TAU,
      spin: (this.rand() < 0.5 ? -1 : 1) * (Math.PI + this.rand() * Math.PI),
      bornMs: nowMs,
      lifeMs: CHIP_LIFE_MS,
      color: css(shades[Math.floor(this.rand() * shades.length)] as Rgb),
      decel: 90,
      endScale: 0,
    });
  }

  /** A floating label over the own head; returns it. */
  label(
    text: string,
    color: Rgb,
    nowMs: number,
    lifeMs: number = ARENA_CLIENT.labelMs,
    size = 1,
  ): FloatingLabel {
    let slot = 0;
    for (const l of this.labels) if (l.bornMs === nowMs && l.slot >= slot) slot = l.slot + 1;
    const l: FloatingLabel = { text, color, bornMs: nowMs, lifeMs, slot, size };
    this.labels.push(l);
    return l;
  }

  /** The capture flash over `cells` (grid indices). */
  captureFlash(cells: readonly number[], cols: number, nowMs: number): void {
    if (ARENA_CLIENT.captureFlashMs <= 0 || cells.length === 0) return;
    this.flash = { polys: cellSetContours(cells, cols), atMs: nowMs };
  }

  /** Advances and drops finished effects. */
  update(nowMs: number, dtMs: number): void {
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;
    for (const list of [this.particles, this.chips]) {
      let w = 0;
      for (let i = 0; i < list.length; i++) {
        const p = list[i] as Particle;
        if (nowMs - p.bornMs >= p.lifeMs) continue;
        if (p.decel > 0) {
          const v = Math.hypot(p.vx, p.vy);
          const nv = Math.max(0, v - p.decel * dt);
          if (v > 0) {
            p.vx *= nv / v;
            p.vy *= nv / v;
          }
        }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.angle += p.spin * dt;
        list[w++] = p;
      }
      list.length = w;
    }
    let w = 0;
    for (const l of this.labels) if (nowMs - l.bornMs < l.lifeMs) this.labels[w++] = l;
    this.labels.length = w;
    if (this.flash && nowMs - this.flash.atMs >= ARENA_CLIENT.captureFlashMs) this.flash = null;
    let f = 0;
    for (const x of this.fades) if (nowMs - x.atMs < LAND_FADE_MS) this.fades[f++] = x;
    this.fades.length = f;
  }

  /** Draws particles and chips (world transform: cells → CSS px via `view`). */
  drawParticles(ctx: Ctx2D, view: ArenaView, nowMs: number): void {
    const s = view.scale;
    const ox = view.width / 2 - view.cx * s;
    const oy = view.height / 2 - view.cy * s;
    for (const list of [this.particles, this.chips]) {
      for (const p of list) {
        const t = Math.min(1, (nowMs - p.bornMs) / p.lifeMs);
        const k = 1 - (1 - p.endScale) * t;
        const half = (p.size * k * s) / 2;
        if (half < 0.25) continue;
        const x = ox + p.x * s;
        const y = oy + p.y * s;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = p.color;
        const c = Math.cos(p.angle);
        const sn = Math.sin(p.angle);
        ctx.beginPath();
        ctx.moveTo(x + (-half * c - -half * sn), y + (-half * sn + -half * c));
        ctx.lineTo(x + (half * c - -half * sn), y + (half * sn + -half * c));
        ctx.lineTo(x + (half * c - half * sn), y + (half * sn + half * c));
        ctx.lineTo(x + (-half * c - half * sn), y + (-half * sn + half * c));
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  /** The capture flash (call between the land top and the trails). */
  drawFlash(ctx: Ctx2D, view: ArenaView, nowMs: number, liftPx: number): void {
    const f = this.flash;
    if (!f) return;
    const age = nowMs - f.atMs;
    if (age >= ARENA_CLIENT.captureFlashMs) return;
    const s = view.scale;
    const ox = view.width / 2 - view.cx * s;
    const oy = view.height / 2 - view.cy * s - liftPx;
    ctx.fillStyle = cssAlpha(0xffffff, ARENA_CLIENT.captureFlashAlpha);
    ctx.beginPath();
    pathPolygons(ctx, f.polys, ox, oy, s);
    ctx.fill();
  }
}
